import React, { useState, useEffect } from "react";
import { doc, onSnapshot, collection, query, orderBy, limit } from "firebase/firestore";
import { db } from "../firebase";

export default function Monitoreo() {
  const [loading, setLoading] = useState(true);
  const [alerts, setAlerts] = useState([]);
  const [logs, setLogs] = useState([]);
  const [syncStatus, setSyncStatus] = useState(null);
  const [autoRefresh, setAutoRefresh] = useState(true);
  const [lastRefresh, setLastRefresh] = useState(null);

  // Cargar alertas recientes desde Firebase
  useEffect(() => {
    const q = query(collection(db, "_alerts"), orderBy("timestamp", "desc"), limit(20));
    const unsubscribe = onSnapshot(q, (snapshot) => {
      const alertsData = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
      setAlerts(alertsData);
      setLoading(false);
    }, (error) => {
      console.error("Error cargando alertas:", error);
      setLoading(false);
    });

    return () => unsubscribe();
  }, []);

  // Cargar logs recientes desde Firebase
  useEffect(() => {
    const q = query(collection(db, "_logs"), orderBy("timestamp", "desc"), limit(50));
    const unsubscribe = onSnapshot(q, (snapshot) => {
      const logsData = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
      setLogs(logsData);
    }, (error) => {
      console.error("Error cargando logs:", error);
    });

    return () => unsubscribe();
  }, []);

  // Cargar estado de sincronización desde Firebase
  useEffect(() => {
    const unsubscribe = onSnapshot(doc(db, "_sync", "buyer"), (doc) => {
      if (doc.exists()) {
        const data = doc.data();
        const lastSync = data.lastSync?.toDate?.() || new Date(data.lastSync);
        const timeSinceSync = Date.now() - lastSync.getTime();
        const minutesAgo = Math.floor(timeSinceSync / 60000);
        
        setSyncStatus({
          lastSync: lastSync.toISOString(),
          timeSinceSync: `${minutesAgo} minutos`,
          lastResult: data.lastResult || null,
          message: minutesAgo > 30 ? "Sincronización antigua (más de 30 min)" : "Sincronización reciente"
        });
      }
      setLoading(false);
    }, (error) => {
      console.error("Error cargando estado de sync:", error);
      setLoading(false);
    });

    return () => unsubscribe();
  }, []);

  // Auto-refresh cada 30 segundos
  useEffect(() => {
    setLastRefresh(new Date());
    if (autoRefresh) {
      const interval = setInterval(() => {
        setLastRefresh(new Date());
      }, 30000);
      return () => clearInterval(interval);
    }
  }, [autoRefresh]);

  const getAlertEmoji = (level) => {
    switch (level) {
      case "critical": return "🚨";
      case "error": return "❌";
      case "warning": return "⚠️";
      default: return "ℹ️";
    }
  };

  const getLogEmoji = (level) => {
    switch (level) {
      case "fatal": return "💀";
      case "error": return "❌";
      case "warn": return "⚠️";
      case "debug": return "🔍";
      default: return "ℹ️";
    }
  };

  if (loading) {
    return (
      <div style={{ display: "flex", justifyContent: "center", alignItems: "center", height: "100vh" }}>
        <div style={{ textAlign: "center" }}>
          <div style={{ fontSize: 24, marginBottom: 16 }}>🔄</div>
          <div style={{ color: "#64748b" }}>Cargando datos de monitoreo...</div>
        </div>
      </div>
    );
  }

  return (
    <div style={{ padding: "24px", maxWidth: "1400px", margin: "0 auto" }}>
      {/* Header */}
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "32px" }}>
        <div>
          <h1 style={{ fontSize: "28px", fontWeight: "bold", marginBottom: "8px", color: "#1e293b" }}>
            🏥 Sistema de Monitoreo
          </h1>
          <p style={{ color: "#64748b", fontSize: "14px" }}>
            Estado de sincronización y alertas del sistema
          </p>
        </div>
        <div style={{ display: "flex", gap: "12px", alignItems: "center" }}>
          {lastRefresh && (
            <span style={{ fontSize: "12px", color: "#94a3b8" }}>
              Última actualización: {lastRefresh.toLocaleTimeString()}
            </span>
          )}
          <label style={{ display: "flex", alignItems: "center", gap: "8px", fontSize: "14px" }}>
            <input
              type="checkbox"
              checked={autoRefresh}
              onChange={(e) => setAutoRefresh(e.target.checked)}
              style={{ cursor: "pointer" }}
            />
            Auto-refresh (30s)
          </label>
        </div>
      </div>

      {/* Sync Status */}
      {syncStatus && (
        <div style={{
          backgroundColor: "white",
          borderRadius: "8px",
          padding: "16px",
          marginBottom: "24px",
          boxShadow: "0 1px 3px rgba(0,0,0,0.1)"
        }}>
          <h3 style={{ fontSize: "16px", fontWeight: "bold", marginBottom: "12px", color: "#1e293b" }}>
            🔄 Estado de Sincronización Buyer → CRM
          </h3>
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(200px, 1fr))", gap: "16px", fontSize: "14px" }}>
            <div>
              <span style={{ color: "#64748b" }}>Última sync:</span>
              <span style={{ marginLeft: "8px", fontWeight: "bold" }}>
                {syncStatus.lastSync 
                  ? new Date(syncStatus.lastSync).toLocaleString() 
                  : "N/A"}
              </span>
            </div>
            <div>
              <span style={{ color: "#64748b" }}>Tiempo desde sync:</span>
              <span style={{ marginLeft: "8px", fontWeight: "bold" }}>
                {syncStatus.timeSinceSync || "N/A"}
              </span>
            </div>
            <div>
              <span style={{ color: "#64748b" }}>Estado:</span>
              <span style={{ marginLeft: "8px", fontWeight: "bold" }}>
                {syncStatus.message}
              </span>
            </div>
          </div>
          {syncStatus.lastResult && (
            <div style={{ marginTop: "12px", padding: "12px", backgroundColor: "#f8fafc", borderRadius: "6px" }}>
              <div style={{ fontSize: "12px", color: "#64748b", marginBottom: "8px" }}>Último resultado:</div>
              <pre style={{ fontSize: "12px", margin: 0, overflow: "auto" }}>
                {JSON.stringify(syncStatus.lastResult, null, 2)}
              </pre>
            </div>
          )}
        </div>
      )}

      {/* Recent Alerts */}
      <div style={{ marginBottom: "24px" }}>
        <h3 style={{ fontSize: "18px", fontWeight: "bold", marginBottom: "16px", color: "#1e293b" }}>
          🚨 Alertas Recientes ({alerts.length})
        </h3>
        <div style={{
          backgroundColor: "white",
          borderRadius: "8px",
          boxShadow: "0 1px 3px rgba(0,0,0,0.1)",
          overflow: "hidden"
        }}>
          {alerts.length === 0 ? (
            <div style={{ padding: "24px", textAlign: "center", color: "#64748b" }}>
              No hay alertas recientes ✨
            </div>
          ) : (
            <div style={{ maxHeight: "400px", overflow: "auto" }}>
              {alerts.map((alert) => (
                <div
                  key={alert.id}
                  style={{
                    padding: "16px",
                    borderBottom: "1px solid #e2e8f0",
                    borderLeft: `4px solid ${
                      alert.level === "critical" ? "#ef4444" :
                      alert.level === "error" ? "#f97316" :
                      alert.level === "warning" ? "#eab308" : "#3b82f6"
                    }`
                  }}
                >
                  <div style={{ display: "flex", justifyContent: "space-between", alignItems: "start", marginBottom: "8px" }}>
                    <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
                      <span style={{ fontSize: "20px" }}>{getAlertEmoji(alert.level)}</span>
                      <span style={{ fontWeight: "bold", color: "#1e293b" }}>{alert.message}</span>
                    </div>
                    <span style={{ fontSize: "12px", color: "#64748b" }}>
                      {new Date(alert.timestamp).toLocaleString()}
                    </span>
                  </div>
                  {alert.details && Object.keys(alert.details).length > 0 && (
                    <div style={{ marginTop: "8px" }}>
                      <details>
                        <summary style={{ cursor: "pointer", fontSize: "12px", color: "#64748b" }}>
                          Ver detalles
                        </summary>
                        <pre style={{ marginTop: "8px", fontSize: "12px", padding: "8px", backgroundColor: "#f8fafc", borderRadius: "4px", overflow: "auto" }}>
                          {JSON.stringify(alert.details, null, 2)}
                        </pre>
                      </details>
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Recent Logs */}
      <div>
        <h3 style={{ fontSize: "18px", fontWeight: "bold", marginBottom: "16px", color: "#1e293b" }}>
          📋 Logs Recientes ({logs.length})
        </h3>
        <div style={{
          backgroundColor: "white",
          borderRadius: "8px",
          boxShadow: "0 1px 3px rgba(0,0,0,0.1)",
          overflow: "hidden"
        }}>
          {logs.length === 0 ? (
            <div style={{ padding: "24px", textAlign: "center", color: "#64748b" }}>
              No hay logs recientes
            </div>
          ) : (
            <div style={{ maxHeight: "400px", overflow: "auto" }}>
              {logs.map((log) => (
                <div
                  key={log.id}
                  style={{
                    padding: "12px",
                    borderBottom: "1px solid #e2e8f0",
                    fontSize: "13px"
                  }}
                >
                  <div style={{ display: "flex", alignItems: "center", gap: "8px", marginBottom: "4px" }}>
                    <span>{getLogEmoji(log.level)}</span>
                    <span style={{ fontWeight: "bold", color: "#1e293b" }}>{log.category?.toUpperCase()}</span>
                    <span style={{ color: "#64748b" }}>{new Date(log.timestamp).toLocaleString()}</span>
                  </div>
                  <div style={{ color: "#334155", marginBottom: "4px" }}>{log.message}</div>
                  {log.data && Object.keys(log.data).length > 0 && (
                    <details>
                      <summary style={{ cursor: "pointer", fontSize: "11px", color: "#64748b" }}>
                        Ver datos
                      </summary>
                      <pre style={{ marginTop: "4px", fontSize: "11px", padding: "4px", backgroundColor: "#f8fafc", borderRadius: "4px", overflow: "auto" }}>
                        {JSON.stringify(log.data, null, 2)}
                      </pre>
                    </details>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}